// Pattern Sets: a SET-style deck generalized to k values per attribute. A group is k cards
// where every attribute is all-same or all-different. k = 3 at the lower rungs, 4 at the top.

import { shuffle, type Rng } from '@kit/rng';

export type Card = number[];   // one value (0..k-1) per attribute

export const ATTR_NAMES = ['color', 'shape', 'fill', 'count'] as const;
export const COLORS = ['#e2574c', '#5fbf6a', '#9b6be6', '#4f8fe6'];
export const SHAPES = ['circle', 'square', 'triangle', 'diamond'] as const;
export const FILLS = ['solid', 'striped', 'empty', 'dots'] as const;

export interface BoardSpec { attrs: number; k: number; cols: number; rows: number; setsToFind: number }

export function specFor(level: number): BoardSpec {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) return { attrs: 2, k: 3, cols: 3, rows: 3, setsToFind: 3 };
  if (L <= 3) return { attrs: 3, k: 3, cols: 3, rows: 4, setsToFind: 4 };
  if (L <= 5) return { attrs: 3, k: 3, cols: 4, rows: 4, setsToFind: 5 };
  if (L <= 7) return { attrs: 4, k: 3, cols: 4, rows: 5, setsToFind: 6 };
  if (L <= 9) return { attrs: 3, k: 4, cols: 6, rows: 6, setsToFind: 6 };
  return { attrs: 4, k: 4, cols: 7, rows: 7, setsToFind: 7 };
}

export function fullDeck(attrs: number, k = 3): Card[] {
  const n = Math.pow(k, attrs);
  const deck: Card[] = [];
  for (let i = 0; i < n; i++) {
    const c: Card = [];
    let v = i;
    for (let a = 0; a < attrs; a++) { c.push(v % k); v = Math.floor(v / k); }
    deck.push(c);
  }
  return deck;
}

/** every attribute all-same or all-different across the group */
export function isGroup(cards: Card[], k: number): boolean {
  if (cards.length !== k) return false;
  for (let a = 0; a < cards[0].length; a++) {
    const distinct = new Set(cards.map(c => c[a])).size;
    if (distinct !== 1 && distinct !== k) return false;
  }
  return true;
}

/** legacy 3-card check kept for tests and callers */
export function isSet(a: Card, b: Card, c: Card): boolean { return isGroup([a, b, c], 3); }

/** which attributes break the rule (for hints) */
export function brokenAttrs(cards: Card[], k: number): number[] {
  const out: number[] = [];
  for (let a = 0; a < cards[0].length; a++) {
    const distinct = new Set(cards.map(c => c[a])).size;
    if (distinct !== 1 && distinct !== k) out.push(a);
  }
  return out;
}

/** all groups on the board; stops after `limit` to stay fast on big boards */
export function findGroups(board: (Card | null)[], k: number, limit = 50): number[][] {
  const idx: number[] = [];
  board.forEach((c, i) => { if (c) idx.push(i); });
  const out: number[][] = [];
  const pick: number[] = [];
  const rec = (start: number) => {
    if (out.length >= limit) return;
    if (pick.length === k) { if (isGroup(pick.map(i => board[i]!), k)) out.push(pick.slice()); return; }
    for (let i = start; i < idx.length; i++) {
      pick.push(idx[i]);
      // prune: with k values per attribute, a partial group must still be extendable:
      // any attribute with between 2 and k-1 distinct values that already repeats a value is dead
      if (prunable(pick.map(j => board[j]!), k)) { pick.pop(); continue; }
      rec(i + 1);
      pick.pop();
    }
  };
  rec(0);
  return out;
}

function prunable(cards: Card[], k: number): boolean {
  if (cards.length < 2) return false;
  for (let a = 0; a < cards[0].length; a++) {
    const vals = cards.map(c => c[a]);
    const distinct = new Set(vals).size;
    // all-same so far: fine. all-different so far: fine. mixed (some repeat, some differ): dead.
    if (distinct !== 1 && distinct !== vals.length) return true;
  }
  return false;
}

export function findSets(board: (Card | null)[]): [number, number, number][] {
  return findGroups(board, 3) as [number, number, number][];
}

export function countGroups(board: (Card | null)[], k: number): number {
  return findGroups(board, k, 1000).length;
}

export function countSets(board: (Card | null)[]): number { return countGroups(board, 3); }

/** Deal a board with at least one group. Returns board and the remaining deck. */
export function deal(spec: BoardSpec, rng: Rng): { board: Card[]; deck: Card[] } {
  const size = spec.cols * spec.rows;
  for (let tries = 0; tries < 50; tries++) {
    const deck = shuffle(rng, fullDeck(spec.attrs, spec.k));
    const board = deck.slice(0, Math.min(size, deck.length));
    const rest = deck.slice(board.length);
    if (findGroups(board, spec.k, 1).length >= 1) return { board, deck: rest };
  }
  const deck = shuffle(rng, fullDeck(spec.attrs, spec.k));
  return { board: deck.slice(0, size), deck: deck.slice(size) };
}

export function sameKey(c: Card): string { return c.join(''); }
